// =============================================================================
// CONSOLIDATED AI CHAT STYLES
// =============================================================================
// This file consolidates 5 style files into a single source:
// - composer.js (281 lines)
// - message.js (255 lines)
// - attachments.js (198 lines)
// - style-utilities.js (138 lines)
// - layout.js (61 lines)
// Total: ~933 lines
// =============================================================================

import { styled, Box, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';

// =============================================================================
// SECTION 0: SHARED RADIUS + BORDER LANGUAGE
// =============================================================================
// Every radius and every resting border in the chat pane (and the sidebar that
// slides out of it) resolves through these two helpers, so the surfaces read as
// one system instead of a pile of one-off numbers.
//
// Radius hierarchy. Outer containers are always rounder than what nests
// inside them:
//   composer (lg, 20px)  the input surface, the most prominent container
//   card     (md, 16px)  top-level cards, panels, dialogs, user message bubbles
//   nested   (sm, 12px)  rows, tiles, and surfaces that sit inside a card
//   chip     (xs,  8px)  chips, badges, tags, inline labels
//   pill     (999px)     fully rounded controls (model picker, send, status)
//   circle   (50%)       circular icon buttons and status dots
//
// The values are pixel STRINGS on purpose. In an MUI `sx` prop a bare number is
// multiplied by theme.shape.borderRadius (16 here), so a radius written as the
// number 10 resolves to 160px while the string "10px" resolves to 10px.
// Emitting strings from the token removes that trap entirely.
//
// The fallbacks mirror `radii` in shared/design/theme.js and only apply when a
// component renders outside the Restack ThemeProvider (bare MUI default theme,
// which some unit tests use).
const RADII_FALLBACK = { xs: 8, sm: 12, md: 16, lg: 20, pill: '999px', circle: '50%' };

export const getChatRadii = (theme) => {
  const radii = { ...RADII_FALLBACK, ...(theme?.restack?.tokens?.radii || {}) };
  return {
    composer: `${radii.lg}px`,
    card: `${radii.md}px`,
    nested: `${radii.sm}px`,
    chip: `${radii.xs}px`,
    pill: radii.pill,
    circle: radii.circle
  };
};

// One resting border for every calm surface (cards, panels, composer) and one
// emphasis border for neutral selected/active/hover states. Semantic state
// borders keep their own hue (success, warning, error, the running accent) and
// only borrow the widths: 1px standard, 2px for genuine emphasis.
export const getChatBorders = (theme) => {
  const borders = theme?.restack?.tokens?.borders;
  const isDark = theme?.palette?.mode === 'dark';
  const textPrimary = theme?.palette?.text?.primary || (isDark ? '#ffffff' : '#000000');
  const width = borders?.widths?.standard ?? 1;
  const emphasisWidth = borders?.widths?.emphasis ?? 2;
  const restingColor = borders?.colors?.resting || alpha(textPrimary, isDark ? 0.24 : 0.16);
  const emphasisColor = borders?.colors?.emphasis || alpha(textPrimary, isDark ? 0.4 : 0.3);
  return {
    resting: borders?.resting || `${width}px solid ${restingColor}`,
    restingColor,
    emphasis: borders?.emphasis || `${emphasisWidth}px solid ${emphasisColor}`,
    emphasisColor,
    width,
    emphasisWidth
  };
};

// =============================================================================
// SECTION 1: COMPOSER STYLES
// Original File: composer.js
// =============================================================================

const resolveControlSurfaceColors = (theme, disabled) => {
  const isDark = theme.palette.mode === 'dark';
  // Composer controls sit on the composer, so they share its resting border.
  const borderColor = getChatBorders(theme).restingColor;
  const enabledBackground = isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.04);
  const disabledBackground = isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.08);
  const hoverBackground = isDark ? alpha('#ffffff', 0.16) : alpha('#0f172a', 0.12);
  const foreground = isDark ? alpha('#ffffff', 0.9) : alpha('#0f172a', 0.75);

  return {
    borderColor,
    backgroundColor: disabled ? disabledBackground : enabledBackground,
    hoverBackground,
    foreground,
    focusOutline: alpha(theme.palette.primary.main, isDark ? 0.6 : 0.4)
  };
};

export const ChatInput = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  // Eight-pixel side margins keep the composer on Fluent's four-pixel grid
  // while preserving almost the full width of a narrow Office task pane.
  width: 'min(calc(100% - 16px), 960px)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.restack.tokens.spacing.sm),
  background: 'transparent',
  border: 'none',
  padding: 0,
  // Make wider on mobile when in home hero or landing hero
  '.home-hero__composer-wrapper &, .home-hero &, .landing-hero__input-wrapper &, .landing-hero &': {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
    }
  }
}));

export const InputContainer = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(tokens.spacing.nano)
  };
});

export const InputRowContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLoading'
})(({ theme, isLoading }) => {
  const accentWarm = theme.palette.accentBubble?.warm || '#D4694E';
  return {
    // The composer is the one surface that carries the largest radius.
    borderRadius: getChatRadii(theme).composer,
    backgroundColor: 'transparent',
    border: getChatBorders(theme).resting,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    position: 'relative',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:focus-within': {
      borderColor: alpha(accentWarm, theme.palette.mode === 'dark' ? 0.35 : 0.30),
      boxShadow: `0 0 0 2px ${alpha(accentWarm, theme.palette.mode === 'dark' ? 0.08 : 0.06)}`,
    },
    // Liveness signal for long-running turns (can take 10–15 min). A slow
    // breathing glow on the input frame tells the user the request is still
    // active even when no tokens are streaming. Reuses the focus-within
    // colors so the visual language stays consistent.
    ...(isLoading && {
      animation: 'restackInputLivenessGlow 2.4s ease-in-out infinite',
      '@keyframes restackInputLivenessGlow': {
        '0%, 100%': {
          borderColor: alpha(accentWarm, theme.palette.mode === 'dark' ? 0.28 : 0.22),
          boxShadow: `0 0 0 0px ${alpha(accentWarm, 0)}`,
        },
        '50%': {
          borderColor: alpha(accentWarm, theme.palette.mode === 'dark' ? 0.55 : 0.42),
          boxShadow: `0 0 0 2px ${alpha(accentWarm, theme.palette.mode === 'dark' ? 0.14 : 0.10)}`,
        },
      },
      // Focus should still be distinguishable from the idle breathing glow —
      // lock to the final focus styling and cancel the animation when typing.
      '&:focus-within': {
        animation: 'none',
        borderColor: alpha(accentWarm, theme.palette.mode === 'dark' ? 0.55 : 0.45),
        boxShadow: `0 0 0 2px ${alpha(accentWarm, theme.palette.mode === 'dark' ? 0.14 : 0.10)}`,
      },
    }),
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transition: 'none',
    },
  };
});

export const TextareaContainer = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  width: '100%',
  borderRadius: 'inherit',
}));

export const MessageTextarea = styled('textarea')(({ theme }) => {
  return {
    width: '100%',
    resize: 'none',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    // Symmetric 12px padding around the 20px line. The single-line height is
    // then exactly 44px (12 + 20 + 12, border-box), which MUST equal
    // `textareaMinHeight` in hooks.js: the JS autosize pins the element to
    // that inline min-height, so a mismatch crushes or floats the text.
    padding: theme.spacing(1.5),
    fontSize: theme.typography.pxToRem(14),
    lineHeight: '20px',
    letterSpacing: 0,
    color: theme.palette.text.primary,
    // Must match `textareaMaxHeight` in hooks.js (theme.spacing(30)). When
    // CSS max-height is smaller than the JS clamp, JS sets a height that
    // CSS truncates and `overflow: hidden` swallows the rest of the content.
    maxHeight: theme.spacing(30),
    // minHeight removed - controlled by JavaScript in hooks.js for consistent behavior
    '&::placeholder': {
      color: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(0, 0, 0, 0.4)',
      opacity: 1,
    },
    // Taller input on mobile for landing page
    '.landing-hero__input-wrapper &, .landing-hero &': {
      [theme.breakpoints.down('sm')]: {
        minHeight: theme.spacing(7),
        fontSize: '1rem',
      }
    }
  };
});

export const ButtonRowContainer = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
    minWidth: 0,
    // 4px above the controls (the textarea already carries 12px below its
    // text) and 12px below, so the frame reads 12 / text / 16 / controls / 12.
    padding: theme.spacing(0.5, 1.5, 1.5),
    // On mobile within home hero, prevent wrap to keep buttons in one row
    '.home-hero__composer &, .home-hero__composer-wrapper &': {
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'nowrap',
        gap: theme.spacing(0.5),
        padding: theme.spacing(tokens.spacing.sm, tokens.spacing.sm),
        overflowX: 'auto',
      }
    }
  };
});

export const LeftButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0,
  // Reduce gap on mobile within home hero
  '.home-hero__composer &, .home-hero__composer-wrapper &': {
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.25),
    }
  }
}));

export const RightButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: 'auto',
  flexShrink: 0,
  // Reduce gap on mobile within home hero
  '.home-hero__composer &, .home-hero__composer-wrapper &': {
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.25),
    }
  }
}));

export const ChatComposerWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  // Tight bottom gap: the composer sits close to the pane edge so the input
  // reads as anchored rather than floating (was theme.spacing(2) below).
  padding: theme.spacing(0.5, 0)
}));

export const ComposerPlaceholderOverlay = styled(Box)(({ theme }) => {
  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
    zIndex: 2,
    color: theme.palette.mode === 'dark' ? 'rgba(226, 232, 240, 0.55)' : 'rgba(100, 116, 139, 0.62)',
    fontSize: theme.typography.pxToRem(14),
    lineHeight: '20px',
    fontWeight: theme.typography.fontWeightRegular,
    letterSpacing: 0,
    borderRadius: 'inherit',
    // Mirrors MessageTextarea's padding exactly so the overlay text sits on
    // the same baseline as the caret and typed text.
    padding: theme.spacing(1.5),
  };
});

export const ComposerPlaceholderContent = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  maxWidth: '100%',
  opacity: 1,
  transform: 'translateY(0)',
  textAlign: 'left',
}));

export const ComposerPlaceholderCursor = styled('span', {
  shouldForwardProp: (prop) => prop !== 'visible'
})(({ theme, visible }) => ({
  display: 'inline-block',
  marginLeft: theme.spacing(0.35),
  fontFamily: 'inherit',
  fontSize: '1em',
  lineHeight: 1,
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.18s ease',
}));

export const InputIconButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'recording'
})(({ theme, disabled }) => {
  const surface = resolveControlSurfaceColors(theme, disabled);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(4),
    height: theme.spacing(4),
    padding: 0,
    boxSizing: 'border-box',
    font: 'inherit',
    appearance: 'none',
    borderRadius: getChatRadii(theme).pill,
    border: `${getChatBorders(theme).width}px solid ${surface.borderColor}`,
    backgroundColor: surface.backgroundColor,
    color: surface.foreground,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    outline: 'none',
    '& svg, & .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(16)
    },
    '&:hover': {
      backgroundColor: disabled ? surface.backgroundColor : surface.hoverBackground
    },
    '&:focus-visible': {
      outline: `2px solid ${surface.focusOutline}`,
      outlineOffset: theme.spacing(0.25)
    },
    '&.voice-button.recording, &.recording.voice-button': {
      backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
      borderColor: alpha(theme.palette.error.main, 0.4),
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.32 : 0.2)
      }
    }
  };
});

export const ModeSelectorButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, disabled, active }) => {
  const surface = resolveControlSurfaceColors(theme, disabled);
  const isActive = Boolean(active);
  const accentBubble = theme.palette?.accentBubble || {};
  const bubbleBase = accentBubble.base || theme.palette.primary.main;
  const activeBackground = accentBubble.tint || alpha(bubbleBase, theme.palette.mode === 'dark' ? 0.16 : 0.08);
  const activeBorder = accentBubble.border || alpha(bubbleBase, theme.palette.mode === 'dark' ? 0.32 : 0.18);
  const activeColor = accentBubble.text || bubbleBase || alpha(bubbleBase, theme.palette.mode === 'dark' ? 0.9 : 0.8);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.375),
    minHeight: theme.spacing(4),
    padding: theme.spacing(0.25, 0.875),
    // Model picker: a fully rounded control.
    borderRadius: getChatRadii(theme).pill,
    border: `${getChatBorders(theme).width}px solid ${isActive ? activeBorder : surface.borderColor}`,
    backgroundColor: isActive ? activeBackground : surface.backgroundColor,
    color: isActive ? activeColor : surface.foreground,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
    appearance: 'none',
    boxShadow: 'none',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': {
      backgroundColor: disabled
        ? (isActive ? activeBackground : surface.backgroundColor)
        : (isActive
          ? (accentBubble.tintHover || alpha(bubbleBase, theme.palette.mode === 'dark' ? 0.22 : 0.12))
          : surface.hoverBackground)
    },
    '&:focus-visible': {
      outline: `2px solid ${surface.focusOutline}`,
      outlineOffset: theme.spacing(0.25)
    }
  };
});

export const UploadDropdown = styled(Box)(({ theme }) => {
  return {
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.95) : theme.palette.background.paper,
    borderRadius: getChatRadii(theme).card,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
    border: getChatBorders(theme).resting,
    minWidth: theme.spacing(22),
    zIndex: 2000,
    padding: theme.spacing(0.75),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    backdropFilter: 'blur(16px)',
    opacity: 1
  };
});

export const UploadOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  color: theme.palette.text.primary,
  borderRadius: getChatRadii(theme).nested,
  transition: 'all 0.15s ease',
  position: 'relative',
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.05),
  },
  '& svg, & .MuiSvgIcon-root': {
    fontSize: theme.typography.pxToRem(18),
    color: alpha(theme.palette.text.secondary, 0.9)
  },
  '& span': {
    ...theme.restack.tokens.typography.bodySm,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.4
  }
}));

// =============================================================================
// SECTION 2: MESSAGE STYLES
// Original File: message.js
// =============================================================================

export const Message = styled(Box, {
  shouldForwardProp: (prop) => !['sender', 'centered'].includes(prop)
})(({ sender, centered = false }) => {
  const isUser = sender === 'user';
  return {
    display: 'flex',
    justifyContent: centered ? 'center' : (isUser ? 'flex-end' : 'flex-start'),
    width: '100%'
  };
});

export const MessageContent = styled(Box, {
  shouldForwardProp: (prop) => !['sender', 'centered'].includes(prop)
})(({ theme, sender, centered = false }) => {
  const { tokens } = theme.restack;
  const isUser = sender === 'user';
  const spacing = getChatSpacing(theme);
  const bubbleBackground = alpha(
    theme.palette.primary.main,
    theme.palette.mode === 'dark' ? 0.16 : 0.08
  );

  return {
    maxWidth: isUser ? '82%' : '100%',
    alignSelf: centered ? 'center' : (isUser ? 'flex-end' : 'flex-start'),
    // A user bubble is a top-level card on the transcript.
    borderRadius: isUser ? getChatRadii(theme).card : 0,
    padding: isUser
      ? theme.spacing(tokens.spacing.sm, tokens.spacing.md)
      : 0,
    backgroundColor: isUser ? bubbleBackground : 'transparent',
    color: theme.palette.text.primary,
    boxShadow: 'none',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(isUser ? spacing.nano : spacing.quark)
  };
});

export const UserAttachmentList = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(tokens.spacing.nano + tokens.spacing.quark),
    marginTop: theme.spacing(tokens.spacing.nano)
  };
});

export const UserAttachmentChip = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(tokens.spacing.nano),
    padding: theme.spacing(tokens.spacing.nano, tokens.spacing.sm),
    borderRadius: getChatRadii(theme).chip,
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(12),
    maxWidth: '100%',
    wordBreak: 'break-word',
    '& .attachment-icon': {
      fontSize: theme.typography.pxToRem(12)
    }
  };
});

export const MessageHeader = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(tokens.spacing.sm)
  };
});

export const Timestamp = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.labelXs,
  color: alpha(theme.palette.text.secondary, 0.9)
}));

export const AgentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(theme.restack.tokens.spacing.xs)
}));

export const AgentStatusPill = styled(Box)(({ theme, completed }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: getChatRadii(theme).pill,
  backgroundColor: completed
    ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.28 : 0.18)
    : alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.3 : 0.2),
  color: completed
    ? (theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark)
    : (theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark),
  ...theme.restack.tokens.typography.bodySm,
  fontWeight: theme.typography.fontWeightRegular
}));

export const AgentBody = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasTimeline'
})(({ theme, hasTimeline }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.restack.tokens.spacing.sm),
  position: 'relative',
  paddingLeft: hasTimeline ? theme.spacing(theme.restack.tokens.spacing.lg) : 0
}));

export const AgentDivider = styled('hr')(({ theme }) => ({
  border: 'none',
  // Drawn as a background rather than a border, but it is a separator, so it
  // matches every other separator in the pane.
  height: getChatBorders(theme).width,
  backgroundColor: theme.palette.divider,
  margin: theme.spacing(theme.restack.tokens.spacing.sm, 0)
}));

export const AgentSummaryContainer = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.05),
  borderRadius: getChatRadii(theme).nested,
  padding: theme.spacing(theme.restack.tokens.spacing.sm)
}));

export const AgentSummaryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(theme.restack.tokens.spacing.sm)
}));

export const AgentSummaryTitle = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.bodySm,
  fontWeight: theme.typography.fontWeightMedium
}));

export const AgentSummaryToggle = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  cursor: 'pointer',
  ...theme.restack.tokens.typography.labelXs
}));

export const AgentSummaryList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2.4),
  color: alpha(theme.palette.text.primary, 0.95),
  ...theme.restack.tokens.typography.bodySm,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  listStyle: 'disc',
  listStylePosition: 'outside'
}));

export const AgentTimelineWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.restack.tokens.spacing.sm),
  position: 'relative'
}));


export const AgentStep = styled(Box, {
  shouldForwardProp: (prop) => !['delay', 'hasTimeline', 'isActive'].includes(prop)
})(({ theme, delay = 0, hasTimeline, isActive }) => ({
  display: 'block',
  paddingTop: 0,
  // The resting state is VISIBLE. The whole assistant message body renders in
  // here, so anything that stops the entrance animation from running must not
  // leave the transcript blank. Fill mode "both" applies the 0% frame through
  // the delay and the 100% frame after, so the reveal still plays identically.
  opacity: 1,
  transform: 'none',
  animation: `${agentStepReveal} 320ms cubic-bezier(0.22, 1, 0.36, 1) both`,
  animationDelay: `${delay}ms`,
  position: 'relative',
  zIndex: 1,
  '@media (prefers-reduced-motion: reduce)': {
    opacity: 1,
    transform: 'none',
    animation: 'none',
  },
}));

export const AgentStepMarker = styled(Box, {
  shouldForwardProp: (prop) => !['isFirst', 'isLast', 'isActive', 'hideMarker'].includes(prop)
})(({ theme, hideMarker }) => ({
  width: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: theme.spacing(0.3),
  paddingBottom: 0,
  marginTop: 0,
  visibility: hideMarker ? 'hidden' : 'visible'
}));

export const AgentStepDot = styled(Box, {
  shouldForwardProp: (prop) => !['isActive', 'isComplete', 'dotColor'].includes(prop)
})(({ theme, isActive = false, isComplete = false, dotColor }) => {
  const color = dotColor || alpha(theme.palette.text.secondary, 0.35);

  return {
    width: theme.spacing(0.6),
    height: theme.spacing(0.6),
    borderRadius: getChatRadii(theme).circle,
    background: color,
    flexShrink: 0,
    boxShadow: 'none',
    position: 'relative'
  };
});

export const AgentStepContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive'
})(({ theme, isActive }) => {
  const tokens = theme.restack?.tokens ?? {};
  const baseGap = tokens.spacing?.nano ?? 1;
  return {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(baseGap),
    position: 'relative',
    padding: 0,
    borderRadius: getChatRadii(theme).nested
  };
});

// =============================================================================
// SECTION 3: ATTACHMENT STYLES
// Original File: attachments.js
// =============================================================================

export const FilePreviewContainer = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.75),
    // Sits INSIDE the input container directly above the textarea, so the
    // strip + textarea read as one unit. A small bottom padding gives the
    // scrollbar breathing room without a visible margin gap.
    paddingTop: theme.spacing(tokens.spacing.sm),
    paddingLeft: theme.spacing(tokens.spacing.sm),
    paddingRight: theme.spacing(tokens.spacing.sm),
    paddingBottom: theme.spacing(0.5),
    overflowX: 'auto',
    overflowY: 'hidden',
    '&::-webkit-scrollbar': {
      height: theme.spacing(0.375) // 3px for sleek appearance
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(
        theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
        theme.palette.mode === 'dark' ? 0.2 : 0.15
      ),
      borderRadius: getChatRadii(theme).pill,
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
          theme.palette.mode === 'dark' ? 0.3 : 0.25
        )
      }
    }
  };
});

// Horizontal chip layout: square icon/thumbnail on the left, filename + size
// stacked on the right, close button inline on the far right. Auto-width up
// to ~260px so a long filename ellipses cleanly without forcing the chip to
// span the whole input. Matches the visual rhythm of modern chat attachments
// (Slack/Notion/Linear) instead of the previous vertical-tile look.
export const FilePreviewItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.875),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  borderRadius: getChatRadii(theme).nested,
  border: getChatBorders(theme).resting,
  backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.85 : 1),
  minWidth: 0,
  maxWidth: theme.spacing(28),
  overflow: 'hidden'
}));

export const FilePreviewImage = styled('img')(({ theme }) => ({
  // 32×32 thumbnail — small enough to read as a chip element, not a card hero.
  width: theme.spacing(4),
  height: theme.spacing(4),
  objectFit: 'cover',
  borderRadius: getChatRadii(theme).chip,
  flexShrink: 0
}));

export const FilePreviewIcon = styled(Box)(({ theme }) => ({
  // 32×32 box matching the image preview size, with a smaller 18-20px glyph
  // tinted in the file-type's identity color (primary by default).
  width: theme.spacing(4),
  height: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: getChatRadii(theme).chip,
  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
  color: theme.palette.primary.main,
  flexShrink: 0,
  '& .MuiSvgIcon-root': {
    fontSize: theme.typography.pxToRem(20)
  }
}));

export const FileInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing(0.125),
  flex: 1,
  minWidth: 0,
  paddingRight: theme.spacing(0.5)
}));

export const FileName = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.bodySm,
  fontSize: theme.typography.pxToRem(12),
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.primary,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

export const FileDetails = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.bodySm,
  fontSize: theme.typography.pxToRem(10),
  fontWeight: theme.typography.fontWeightRegular,
  color: alpha(theme.palette.text.secondary, 0.78),
  fontVariantNumeric: 'tabular-nums',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.3
}));

export const TimerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(theme.restack.tokens.spacing.nano),
  color: theme.palette.text.secondary
}));

export const TimerText = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.bodySm,
  color: theme.palette.text.secondary
}));

export const UnifiedCardContainer = styled(Box)(({ theme }) => {
  return {
    border: getChatBorders(theme).resting,
    borderRadius: getChatRadii(theme).card,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    boxShadow: 'none',
    boxSizing: 'border-box'
  };
});

export const UnifiedCardHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'centerTitle'
})(({ theme, centerTitle }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: centerTitle ? 'center' : 'space-between',
  padding: theme.spacing(theme.restack.tokens.spacing.sm, theme.restack.tokens.spacing.md),
  gap: theme.spacing(theme.restack.tokens.spacing.md)
}));

export const UnifiedCardTitle = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.bodySm,
  fontWeight: theme.typography.fontWeightMedium,
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(theme.restack.tokens.spacing.nano),
  minWidth: 0,  // Allow shrinking below content size
  flex: 1,       // Grow to fill available space
  wordWrap: 'break-word',
  overflowWrap: 'break-word'
}));

export const UnifiedCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.restack.tokens.spacing.sm, theme.restack.tokens.spacing.md),
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  minWidth: 0  // Allow content to shrink below its natural width
}));

export const ChatHistorySidebar = styled(Box, {
  shouldForwardProp: (prop) => !['open', 'wide'].includes(prop)
})(({ theme, open, wide }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  // `wide` expands the drawer for editing surfaces (the workflow editor's
  // multiline instructions are unreadable at list width).
  width: wide ? '96%' : '88%',
  maxWidth: wide ? 560 : 320,
  height: '100%',
  transform: open ? 'translateX(0)' : 'translateX(-100%)',
  transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s ease, max-width 0.25s ease',
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.default, 0.92)
    : alpha(theme.palette.background.paper, 0.97),
  backdropFilter: 'blur(20px)',
  // Structural separator between the sidebar and the transcript behind it, so
  // it uses the divider token rather than a surface border.
  borderRight: `${getChatBorders(theme).width}px solid ${theme.palette.divider}`,
  boxShadow: open ? '4px 0 20px rgba(0,0,0,0.12)' : 'none',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  pointerEvents: open ? 'auto' : 'none',
}));

export const ChatHistorySidebarBackdrop = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: alpha(theme.palette.common.black, 0.2),
  zIndex: 1299,
  opacity: open ? 1 : 0,
  pointerEvents: open ? 'auto' : 'none',
  transition: 'opacity 0.25s ease',
}));

export const ChatHistoryItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  borderRadius: getChatRadii(theme).nested,
  transition: 'all 0.15s ease',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

export const ResearchStatusDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ theme, status }) => {
  const baseColor = status === 'complete'
    ? theme.palette.success.main
    : status === 'running'
      ? theme.palette.info.main
      : theme.palette.text.secondary;

  return {
    width: theme.spacing(0.75),
    height: theme.spacing(0.75),
    borderRadius: getChatRadii(theme).circle,
    backgroundColor: baseColor
  };
});

export const ResearchButton = styled(Box, {
  shouldForwardProp: (prop) => !['isActive', 'isCompact'].includes(prop)
})(({ theme, isActive, isCompact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isCompact ? 0 : theme.spacing(theme.restack.tokens.spacing.nano),
  padding: isCompact
    ? theme.spacing(theme.restack.tokens.spacing.nano, theme.restack.tokens.spacing.xs)
    : theme.spacing(theme.restack.tokens.spacing.nano, theme.restack.tokens.spacing.sm + 0.5),
  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.background.default,
  color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  border: `${getChatBorders(theme).width}px solid ${isActive ? theme.palette.primary.main : getChatBorders(theme).restingColor}`,
  borderRadius: getChatRadii(theme).pill,
  cursor: 'pointer',
  ...theme.restack.tokens.typography.bodyMd,
  fontWeight: theme.typography.fontWeightRegular,
  transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  minWidth: isCompact ? theme.spacing(4) : 'auto',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
    borderColor: isActive ? theme.palette.primary.dark : theme.palette.primary.main
  },
  '& .MuiSvgIcon-root': {
    fontSize: isCompact ? theme.spacing(2) : theme.restack.tokens.typography.bodyMd.fontSize
  }
}));

export const ShimmerBox = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: getChatRadii(theme).nested,
  backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.16 : 0.08)
}));

export const ShimmerText = styled(Typography)(({ theme }) => ({
  borderRadius: getChatRadii(theme).chip,
  backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.06),
  color: 'transparent'
}));

export const ShimmerIcon = styled(Box)(({ theme }) => ({
  width: theme.spacing(2.5),
  height: theme.spacing(2.5),
  borderRadius: getChatRadii(theme).circle,
  backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.1)
}));

// =============================================================================
// SECTION 4: STYLE UTILITIES
// Original File: style-utilities.js
// =============================================================================

export const shimmerAnimation = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

export const rotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const agentStepReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  60% {
    opacity: 1;
    transform: translateY(0) scale(1.01);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const CHAT_PANEL = {
  WIDTH: 575, // Default width for better balance
  HEADER_HEIGHT_UNITS: 5.5
};

export const getChatSpacing = (theme) => {
  const { tokens } = theme.restack;
  return {
    xs: tokens.spacing.xs,
    sm: tokens.spacing.sm,
    md: tokens.spacing.md,
    lg: tokens.spacing.lg,
    nano: tokens.spacing.nano,
    quark: tokens.spacing.quark
  };
};

export const getGuidanceColor = (theme, severity = 'info') => {
  const severityMap = {
    info: theme.palette.info.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    success: theme.palette.success.main
  };
  return severityMap[severity] || severityMap.info;
};

export const GuidanceContainer = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    margin: `${theme.spacing(tokens.spacing.sm)} ${theme.spacing(tokens.spacing.lg)} 0`,
    padding: theme.spacing(tokens.spacing.sm, tokens.spacing.md),
    borderRadius: getChatRadii(theme).card,
    border: `${getChatBorders(theme).width}px solid ${alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.3 : 0.18)}`,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(tokens.spacing.sm)
  };
});

export const GuidanceItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'severity'
})(({ theme, severity = 'info' }) => {
  const { tokens } = theme.restack;
  const color = getGuidanceColor(theme, severity);
  return {
    borderRadius: getChatRadii(theme).nested,
    // Severity rail: keeps its semantic hue, uses the shared emphasis width.
    borderLeft: `${getChatBorders(theme).emphasisWidth}px solid ${color}`,
    padding: theme.spacing(tokens.spacing.xs, tokens.spacing.sm),
    backgroundColor: alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(tokens.spacing.nano)
  };
});

export const GuidanceTitle = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.labelXs,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: alpha(theme.palette.text.primary, 0.7)
}));

export const GuidanceMessage = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.bodySm,
  color: alpha(theme.palette.text.primary, 0.95)
}));

export const GuidanceMeta = styled(Typography)(({ theme }) => ({
  ...theme.restack.tokens.typography.labelXs,
  color: alpha(theme.palette.text.secondary, 0.9),
  letterSpacing: '0.02em'
}));

// =============================================================================
// SECTION 5: LAYOUT STYLES
// Original File: layout.js
// =============================================================================

export const ChatSidebar = styled(Box, {
  shouldForwardProp: (prop) => !['collapsed', 'width', 'position'].includes(prop)
})(({ theme, collapsed, width = CHAT_PANEL.WIDTH, position = 'right' }) => ({
  width: collapsed ? 0 : width,
  minWidth: collapsed ? 0 : width,
  height: '100%',
  backgroundColor: theme.palette.mode === 'dark'
    ? theme.palette.background.default
    : '#FAFAFA',
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  transition: collapsed ? 'width 0.3s ease, min-width 0.3s ease' : 'none',
  overflow: collapsed ? 'hidden' : 'visible',
  position: 'relative',
  borderLeft: (!collapsed && position === 'right')
    ? `${getChatBorders(theme).width}px solid ${theme.palette.divider}`
    : 'none',
  borderRight: (!collapsed && position === 'left')
    ? `${getChatBorders(theme).width}px solid ${theme.palette.divider}`
    : 'none'
}));

export const ChatHeader = styled(Box)(({ theme }) => {
  const { tokens } = theme.restack;
  return {
    height: theme.spacing(CHAT_PANEL.HEADER_HEIGHT_UNITS),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, tokens.spacing.md),
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.background.default
      : '#FAFAFA',
    borderBottom: 'none',
    flexShrink: 0,
    '& .MuiTypography-root': {
      ...tokens.typography.bodySm,
      fontWeight: theme.typography.fontWeightRegular,
      color: theme.palette.text.secondary,
      letterSpacing: 0
    },
    '& .MuiIconButton-root': {
      padding: theme.spacing(tokens.spacing.nano, tokens.spacing.sm),
      borderRadius: getChatRadii(theme).nested,
      '& .MuiSvgIcon-root': {
        fontSize: theme.spacing(2.25), // 18px - match ControlBar and ToolBar
        color: theme.palette.text.secondary
      },
      '&:hover': {
        backgroundColor: tokens.surfaces?.hover || theme.palette.action.hover
      }
    }
  };
});

export const ChatMessages = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isWelcomeView'
})(({ theme, isWelcomeView = false }) => {
  const { tokens } = theme.restack;
  const scrollbarWidth = 0.375; // 3px for sleeker appearance
  const trackColor = alpha(
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    theme.palette.mode === 'dark' ? 0.05 : 0.03
  );
  const thumbColor = alpha(
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    theme.palette.mode === 'dark' ? 0.2 : 0.15
  );
  const thumbHoverColor = alpha(
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    theme.palette.mode === 'dark' ? 0.3 : 0.25
  );

  return {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: theme.spacing(tokens.spacing.md),
    paddingLeft: theme.spacing(tokens.spacing.md), // Right padding removed, handled by MessageWrapper
    display: 'flex',
    flexDirection: 'column',
    justifyContent: isWelcomeView ? 'center' : 'flex-start',
    alignItems: isWelcomeView ? 'center' : 'stretch',
    gap: isWelcomeView ? 0 : theme.spacing(tokens.spacing.nano + tokens.spacing.quark),
    ...(isWelcomeView && {
      paddingTop: theme.spacing(tokens.spacing.md),
      paddingRight: theme.spacing(tokens.spacing.md),
      paddingBottom: theme.spacing(tokens.spacing.md),
      paddingLeft: theme.spacing(tokens.spacing.md)
    }),
    '&::-webkit-scrollbar': {
      width: theme.spacing(scrollbarWidth)
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: trackColor,
      borderRadius: getChatRadii(theme).pill
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumbColor,
      borderRadius: getChatRadii(theme).pill,
      '&:hover': {
        backgroundColor: thumbHoverColor
      }
    }
  };
});

// =============================================================================
// MODULE EXPORTS
// =============================================================================
// All styled components are exported above inline with their definitions
// This consolidated file provides a single source for all AI Chat styles
